import {LitElement, html, css} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {msg} from "@lit/localize";

@customElement('unsplash-image-input')
class UnsplashImagePicker extends LitElement {

    static styles = css`
      :host {
        display: block;
        border: 1px solid var(--colorGray300);
        border-radius: 5px;
        padding: 0.5rem;
        background-color: white;
        position: relative;
      }

      .input-container {
        display: flex;
        gap: 0.5rem;
        margin-top: 0.5rem;
      }

      .input-container input {
        flex: 1;
        border: 1px solid var(--colorGray300);
        border-radius: 5px;
        padding: 0.375rem 0.5rem;
        font-family: dskrptSans, sans-serif;
        font-size: 1rem;
      }

      .input-container button {
        background-color: var(--colorBrandColor);
        font-family: dskrptSans, sans-serif;
        font-weight: 500;
        font-size: var(--fontSizeXS);
        color: white;
        border: none;
        border-radius: 5px;
        padding: 0.375rem 1rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .input-container button svg {
        width: 1rem;
        height: 1rem;
      }

      .images-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        overflow-x: scroll;
        width: 100%;
      }

      .image-container {
        display: flex;
        flex-direction: column;
        width: 7rem;
        border: 1px solid transparent;
        border-radius: 5px;
        padding: 0.125rem;
      }

      .image-container.current {
        background-color: var(--colorGray200);
        color: black;
      }

      .image-container.selected {
        background-color: var(--colorBrandColor);
        color: white;
      }

      .image-label {
        font-size: 0.675rem;
        padding: 0 0.25rem;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 500;
      }

      .image-label a {
        color: inherit;
        text-decoration: none;
      }

      img {
        box-sizing: border-box;
        object-fit: cover;
        cursor: pointer;
        aspect-ratio: 1/1;
        border-bottom: none;
        border-radius: 4px;
      }
    `;

    @property({type: String, attribute: 'current-image-id'}) currentImageId = '';
    @property() selectedImageData: any = {};

    @state() currentImageData: any = undefined;
    @state() searchQuery = '';
    @state() images = [];

    connectedCallback() {
        super.connectedCallback();
        //     Retrieve information for the selected image from Unsplash
        if (this.currentImageId) {
            fetch(`/api/unsplash/?picture_id=${this.currentImageId}`)
                .then((response) => response.json())
                .then((data) => {
                    this.currentImageData = data;
                    this.selectedImageData = data;
                    this.images = [data];
                });
        }
    }

    removeImage() {
        this.selectedImageData = {};
        this.currentImageData = {};
        this.currentImageId = '';
        this.images = [];
        this.dispatchEvent(
            new CustomEvent('image-selected', {
                detail: {imageId: null},
                bubbles: true,
                composed: true
            })
        );
    }

    /** Search images on Unsplash with search query */
    async searchImages() {
        if (!this.searchQuery) return;

        const response = await fetch(
            `/api/unsplash/?query=${encodeURIComponent(this.searchQuery)}`
        );
        const data = await response.json();
        this.images = [this.currentImageData, ...data.results.splice(0, 6)];
    }

    selectImage(image) {
        this.selectedImageData = image;
        this.dispatchEvent(
            new CustomEvent('image-selected', {
                detail: {imageId: image.id},
                bubbles: true,
                composed: true
            })
        );
    }


    renderImage(image) {
        if (image === undefined) {
            return;
        }

        const isCurrentImage = this.currentImageId === image.id;
        const isSelectedImage = this.selectedImageData.id === image.id;
        let label;
        let classes;
        const attribution = html`
            <div class="image-label">
                <a href="${image.user.links.html + '?utm_source=dskrpt.de&utm_medium=referral'}" target="_blank">
                    ${image.user.name}
                </a>
            </div>`
        if (isSelectedImage) {
            classes = {'image-container': true, 'selected': true}
            label = html`
                <div class="image-label selected"><span>${msg('Selected')}</span></div>`
        } else if (isCurrentImage) {
            classes = {'image-container': true, 'current': true}
            label = html`
                <div class="image-label current"><span>${msg('Current')}</span></div>`
        } else {
            classes = {'image-container': true}
        }
        return html`
            <div class="${classMap(classes)}">
                ${label}
                <img alt="${image.alt_description}"
                     src="${image.urls.thumb}"
                     @click=${() => this.selectImage(image)}
                />
                ${attribution}
            </div>
        `
    }

    render() {
        return html`
            <div class="images-container">
                ${this.images.map((image) => this.renderImage(image))}
                ${this.images.length === 0
                        ? html`
                            <div>${msg('No image selected. You can search by keyword below.')}</div>`
                        : undefined}
            </div>
            <div class="input-container">
                <input type="search"
                       placeholder="${msg('Search for images')}"
                    @input=${(e) => (this.searchQuery = e.target.value)}
                    @keydown=${(e) => e.key === 'Enter' ? this.searchImages() : undefined}
                />
                <button @click=${this.searchImages}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                        <path fill-rule="evenodd"
                              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                              clip-rule="evenodd"/>
                    </svg>
                    ${msg('Search')}
                </button>
                <button @click=${this.removeImage}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>
                    </svg>
                    ${msg('Remove image')}
                </button>
            </div>
        `;
    }
}

export default UnsplashImagePicker;
